import { TourStopTrackingCheckInPayload } from './../../../models/payload-models/tour-stop-tracking-update-payload.model';
import { Inject, Injectable, Injector } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
  ConfType,
  ShiftEditingPayload,
  ShiftBreakEditingPayload,
  StartShiftPayload,
  EndShiftPayload,
  TourStopTrackingEditingPayload,
  TourStopTrackingCheckOutPayload
} from '@models/*';

import moment from 'moment';
import { RestService } from '../../core/services/rest.service';
import { ToastService } from '../../core/services/toast.service';
import { ErrorService } from '../../core/services/error.service';
import { NetworkService } from '../../core/services/network.service';
import { APP_CONFIG, AppConfig } from '../../config/app-config';

@Injectable({
    providedIn: 'root'
})
export class ShiftService extends RestService {
  constructor(
    @Inject(APP_CONFIG) appConfig: AppConfig,
    private toastService: ToastService,
    http: HttpClient,
    errorService: ErrorService,
    networkService: NetworkService,
    injector: Injector,
  ) {
    super(appConfig, http, errorService, networkService, injector);
  }

    // todo handle all toast generation from effects

    getReportShift(datePayload: string): Observable<any> {
        return this.get(`${this.REPORT_SHIFT_URL}?date=${encodeURIComponent(datePayload)}`)
            .pipe(
                map(res => (res instanceof HttpErrorResponse || res.error) ? [] : res.data)
            );
    }

    startShift(payload: StartShiftPayload): Observable<any> {
        payload.timestamp = moment().toISOString(true);
        return this.post(`${this.SHIFT_URL}/start`, payload).pipe(
            map(res => {
                if (res instanceof HttpErrorResponse) {
                    throw new Error(`StartShift failed: ${res?.status} ${res?.message}`);
                }
                return res.data;
            }),
            tap(res => {
                if (res.status && !res.error) {
                    this.toastService.generateSuccessToast(ConfType.START_SHIFT);
                }
            }),
        );
    }

    endShift(payload: EndShiftPayload): Observable<any> {
        payload.timestamp = moment().toISOString(true);
        return this.post(`${this.SHIFT_URL}/end`, payload).pipe(
            map(res => {
                if (res instanceof HttpErrorResponse) {
                    throw new Error(`EndShift failed: ${res?.status} ${res?.message}`);
                }
                return res.data;
            }),
            tap(res => {
                if (res.status && !res.error) {
                    this.toastService.generateSuccessToast(ConfType.END_SHIFT);
                }
            }),
        );
    }

    sendShiftBreakUpdate(payload, breakPhase: 'start' | 'end'): Observable<any> {
        return this.post(`${this.SHIFT_BREAK_URL}`, [payload]).pipe(
            map(res => (res instanceof HttpErrorResponse || res.error) ? null : res),
            tap(res => {
                if (res && res.status && JSON.parse(res.status) && !res.error) {
                    if (breakPhase === 'start') {
                        this.toastService.generateSuccessToast(ConfType.START_BREAK);
                    } else {
                        this.toastService.generateSuccessToast(ConfType.END_BREAK);
                    }
                } else {
                    throw new Error('[Shift Service] - Shift Break Update Fails!');
                }
            })
        );
    }

    tourStopTrackingCheckIn(payload: TourStopTrackingCheckInPayload) {
        return this.post(`${this.TOUR_STOP_TRACKING_CHECK_IN_URL}`, payload).pipe(
            map(res => (res instanceof HttpErrorResponse || res.error) ? null : res),
            tap(res => {
                if (res && res.status && JSON.parse(res.status) && !res.error) {
                    this.toastService.generateSuccessToast(ConfType.START_STOP_TRACKING);
                } else {
                    throw res;
                }
            }),
            catchError( err => {
                throw new Error(`[Shift Service] - tourStopTrackingCheckIn Fails!, Error: ${err}`);
            })
        )
    }

    tourStopTrackingCheckOut(payload: TourStopTrackingCheckOutPayload, hasOpenTSt?: boolean) {
        return this.post(`${this.TOUR_STOP_TRACKING_CHECK_OUT_URL}`, payload).pipe(
            map(res => (res instanceof HttpErrorResponse || res.error) ? null : res),
            tap(res => {
                if (hasOpenTSt) {
                    this.toastService.generateSuccessToast(ConfType.END_STOP_TRACKING_PREVIOUS);
                } else {
                    this.toastService.generateSuccessToast(ConfType.END_STOP_TRACKING);
                }
            }),
            catchError( error  => {
                throw new Error('[Shift Service] - tourStopTrackingCheckOut Fails!');
            })
        )
    }

    editShift(payload: ShiftEditingPayload): Observable<any> {
        return this.post(`${this.SHIFT_URL}/edit`, [payload]).pipe(
            map(res => (res instanceof HttpErrorResponse || res.error) ? null : res),
            tap(res => {
                if (res && res.status && JSON.parse(res.status) && !res.error) {
                    this.toastService.generateSuccessToast(ConfType.EDIT_SHIFT);
                }
            })
        );
    }

    editShiftBreak(payload: ShiftBreakEditingPayload): Observable<any> {
        return this.post(`${this.SHIFT_BREAK_URL}/edit`, [payload]).pipe(
            map(res => (res instanceof HttpErrorResponse || res.error) ? null : res),
            tap(res => {
                if (res && res.status && JSON.parse(res.status) && !res.error) {
                    this.toastService.generateSuccessToast(ConfType.EDIT_SHIFT_BREAK);
                }
            })
        );
    }

    editTourStopTracking(payload: TourStopTrackingEditingPayload): Observable<any> {
        // TODO: finalize rename time register => tour stop tracking (also on backend)
        const httpPayload = {
            ...payload,
            tourStopTrackingId: payload.tourStopTrackingId,
        };
        return this.post(`${this.TOUR_STOP_TRACKING_TRACKING_URL}/edit`, [httpPayload]).pipe(
            map(res => (res instanceof HttpErrorResponse || res.error) ? null : res),
            tap(res => {
                if (res && res.status && JSON.parse(res.status) && !res.error) {
                    this.toastService.generateSuccessToast(ConfType.EDIT_TOUR_STOP_TRACKING);
                }
            })
        );
    }
}
